import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useApi } from "@services/useApi";
import { setMyPermission } from "@store/userDetailSlice";
import { Loader } from "@components/Loader";
import { NavItems } from "@helpers/navigation";
import { useUserPermission } from "@helpers/userPermissionHelper";
import { ManageLanding } from "@fadmin/tabs/manage/pages/manageLanding";
import { ResultsLanding } from "@fadmin/tabs/results/pages/resultsLanding";
import { AdminAssessmentsLanding } from "@fadmin/tabs/assessments/pages/adminAssessmentsLanding";
import { SuperAdminLanding } from "../../../../superadmin/pages/superAdminLanding";
import { AssessmentLanding } from "@fassessment/pages/assessmentLanding";
import { LandingPage } from "../../../../../common/pages/landingPage";
import { PoCLading } from "../../../../poc/pages/pocLading";
import { updateOrganization } from "@store/organizationSlice";
import { apiOrganizationToOrganization } from "@helpers/organizationHelper";
import { updateCurrentLocation, updateLocations } from "@store/locationsSlice";
import { NotAccessLanding } from "../../../../notaccess/pages/notAccessLanding";
import { isEmpty } from "lodash";
import { PurchaseLanding } from "../../../../purchase/pages/purchaseLanding";
import { ContactLanding } from "../../../../signup/pages/contactLanding";
import { DocumentationLanding } from "../../../../documentation/pages/documentationLanding";
import { useLocalStorage, LocalStorageKey } from "@helpers/localStorageHelper";
import { LoginRedirect } from "@store/generalTypes";

export const MainRoot = (): JSX.Element => {
    const [isLoading, setIsLoading] = useState(true);

    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [loginRedirect, , cleanLoginRedirect] = useLocalStorage<LoginRedirect>(LocalStorageKey.LoginRedirect);

    const { getMyPermission, getOrganization, getOrganizationLocations } = useApi();
    const { userDetail, locations, organization, currentLocation } = useAppSelector((x) => {
        return {
            userDetail: x.userDetail,
            locations: x.orgLocations.locations,
            organization: x.organization,
            currentLocation: x.orgLocations.currentLocation
        };
    });

    const {
        hasAccess,
        isSuperAdmin,
        isOrganizationAdmin,
        isClinicalAdmin,
        isFacilitator,
        isClinician,
        isDataQa,
        isResearcher
    } = useUserPermission({});

    useEffect(() => {
        if (userDetail.id) {
            void loadUserAccess();
        } else {
            void getPermissions();
        }
    }, [userDetail]);

    useEffect(() => {
        if (!isLoading) {
            setLocationAccess();
            navigateToUserPage();
        }
    }, [isLoading, pathname]);

    const getPermissions = useCallback(async () => {
        const myPermission = await getMyPermission();
        dispatch(setMyPermission(myPermission));
    }, []);

    const loadUserAccess = useCallback(async () => {
        // Refreshing permissions must not reset an active organization/location selection.
        const selectedOrganization = userDetail.organizations.find((org) => org.id === organization.id);
        if (selectedOrganization && !isLoading) {
            return;
        }

        if (!isEmpty(userDetail.organizations)) {
            const orgId = selectedOrganization?.id ?? userDetail.organizations[0].id;

            if (orgId) {
                const myOrganization = await getOrganization(orgId);
                dispatch(updateOrganization(apiOrganizationToOrganization(myOrganization)));

                const myLocations = await getOrganizationLocations(orgId);
                dispatch(updateLocations(myLocations));

                dispatch(
                    updateCurrentLocation(myLocations.find((loc) => loc.id === currentLocation?.id) ?? myLocations[0])
                );
            }
        } else {
            dispatch(updateLocations([]));
            dispatch(updateCurrentLocation(undefined));
        }

        setIsLoading(false);
    }, [userDetail, organization.id, currentLocation?.id, isLoading]);

    const setLocationAccess = (): void => {
        const myLocations = isOrganizationAdmin
            ? locations
            : locations.filter((x) => userDetail.locations.some((l) => l.id === x.id));

        dispatch(updateLocations(myLocations));
    };

    const navigateToUserPage = (): void => {
        if (hasAccess) {
            if (loginRedirect?.pathname) {
                cleanLoginRedirect();
                navigate(loginRedirect.pathname);
                return;
            }

            if (pathname && pathname !== "/") {
                navigate(pathname + search);
                return;
            }

            if (isClinicalAdmin) {
                navigate(NavItems.Admin.Manage.Overview.path);
                return;
            }

            if (isSuperAdmin) {
                navigate(NavItems.SuperAdmin.Landing.path);
                return;
            }

            if (isFacilitator) {
                navigate(NavItems.Admin.Assessments.Upcoming.path);
                return;
            }

            if (isClinician || isDataQa || isResearcher) {
                navigate(NavItems.Admin.Results.parentRoot.path);
                return;
            }

            navigate(NavItems.Admin.Manage.Overview.path);
        } else {
            navigate(NavItems.Purchase.Pricing.path);
        }
    };

    return isLoading ? (
        <Loader />
    ) : (
        <Routes>
            <Route path={NavItems.Admin.Manage.Overview.wildcardPath} element={<ManageLanding />} />
            <Route path={NavItems.Admin.Results.parentRoot.wildcardPath} element={<ResultsLanding />} />
            <Route path={NavItems.Admin.Assessments.Upcoming.wildcardPath} element={<AdminAssessmentsLanding />} />
            <Route path={NavItems.Documentation.Landing.wildcardPath} element={<DocumentationLanding />} />
            <Route path={NavItems.SuperAdmin.Landing.wildcardPath} element={<SuperAdminLanding />} />
            <Route path={NavItems.Assessments.Landing.wildcardPath} element={<AssessmentLanding />} />
            <Route path={NavItems.Purchase.Landing.wildcardPath} element={<PurchaseLanding />} />
            <Route path={NavItems.Signup.Landing.wildcardPath} element={<ContactLanding />} />

            <Route path={NavItems.NotAccess.Landing.wildcardPath} element={<NotAccessLanding />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path={"/poc"} element={<PoCLading />} />
        </Routes>
    );
};
